/* ==========================================================================
   aesthetic.luxury.lb1 — theme.js
   Cart drawer (AJAX + Section Rendering API), quantity steppers, quick add,
   mobile menu, product gallery, sticky header, scroll reveal.
   No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var routes = (window.theme && window.theme.routes) || {};
  var MIN_FREE = (window.theme && window.theme.freeShippingMinItems) || 2;

  /* ---------------------------------------------------------------- utils */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function on(type, selector, handler) {
    document.addEventListener(type, function (event) {
      var target = event.target.closest(selector);
      if (target) handler(event, target);
    });
  }

  var scrollLock = {
    count: 0,
    lock: function () {
      if (this.count === 0) document.body.classList.add('is-locked');
      this.count++;
    },
    unlock: function () {
      this.count = Math.max(0, this.count - 1);
      if (this.count === 0) document.body.classList.remove('is-locked');
    }
  };

  function trapFocus(container) {
    var focusables = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return function () {};
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    function handle(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handle);
    first.focus();
    return function () { container.removeEventListener('keydown', handle); };
  }

  /* --------------------------------------------------------------- overlay */
  var overlay = { el: null, users: 0 };

  function showOverlay() {
    overlay.el = overlay.el || $('[data-overlay]');
    if (!overlay.el) return;
    overlay.users++;
    overlay.el.hidden = false;
    requestAnimationFrame(function () { overlay.el.classList.add('is-visible'); });
  }

  function hideOverlay() {
    if (!overlay.el) return;
    overlay.users = Math.max(0, overlay.users - 1);
    if (overlay.users > 0) return;
    overlay.el.classList.remove('is-visible');
    setTimeout(function () { if (overlay.users === 0) overlay.el.hidden = true; }, 450);
  }

  /* ----------------------------------------------------------- cart drawer */
  var CartDrawer = {
    lastFocus: null,
    releaseFocus: null,

    get el() { return $('[data-cart-drawer]'); },

    open: function () {
      var el = this.el;
      if (!el) return;
      this.lastFocus = document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      showOverlay();
      scrollLock.lock();
      this.releaseFocus = trapFocus(el);
    },

    close: function () {
      var el = this.el;
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      hideOverlay();
      scrollLock.unlock();
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    /* Replace drawer markup returned by the Section Rendering API */
    render: function (sections) {
      var html = sections && (sections['cart-drawer'] || sections['cart_drawer']);
      if (!html) return this.refresh();

      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = $('[data-cart-drawer-contents]', parsed);
      var current = $('[data-cart-drawer-contents]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      this.syncCount(parsed);
      this.syncCartPage();
    },

    /* Fallback: re-fetch the drawer section on its own */
    refresh: function () {
      var self = this;
      return fetch('?sections=cart-drawer', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { self.render(data); })
        .catch(function () { window.location.href = routes.cart_url || '/cart'; });
    },

    syncCount: function (scope) {
      var source = $('[data-cart-count-source]', scope || document);
      if (!source) return;
      var count = parseInt(source.getAttribute('data-cart-count-source'), 10) || 0;
      $$('[data-cart-count]').forEach(function (bubble) {
        bubble.textContent = count > 99 ? '99+' : count;
        bubble.hidden = count === 0;
      });
      document.documentElement.style.setProperty('--cart-count', count);
    },

    /* The /cart page is a separate section — reload it when it is on screen */
    syncCartPage: function () {
      if (!$('[data-cart-page]')) return;
      fetch('?sections=main-cart', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var html = data['main-cart'];
          if (!html) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = $('[data-cart-page]', parsed);
          var current = $('[data-cart-page]');
          if (fresh && current) current.replaceWith(fresh);
        });
    }
  };

  window.CartDrawer = CartDrawer;

  /* ---------------------------------------------------------- add to cart */
  function setLoading(form, isLoading) {
    var button = $('[data-add-button]', form);
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    button.disabled = isLoading;
  }

  function showFormError(form, message) {
    var box = $('[data-form-error]', form);
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  on('submit', 'form[data-product-form]', function (event, form) {
    event.preventDefault();
    showFormError(form, '');
    setLoading(form, true);

    var body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);

    fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error(result.data.description || result.data.message || 'Unable to add this item.');
        }
        CartDrawer.render(result.data.sections);
        CartDrawer.open();
      })
      .catch(function (error) {
        showFormError(form, error.message);
      })
      .finally(function () {
        setLoading(form, false);
      });
  });

  /* ------------------------------------------------------ cart line edits */
  var pendingChange = null;

  function changeLine(line, quantity) {
    if (pendingChange) return;
    var drawer = $('[data-cart-drawer-contents]');
    if (drawer) drawer.setAttribute('aria-busy', 'true');

    pendingChange = fetch(routes.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { CartDrawer.render(data.sections); })
      .catch(function () { window.location.reload(); })
      .finally(function () {
        pendingChange = null;
        if (drawer) drawer.removeAttribute('aria-busy');
      });
  }

  on('click', '[data-line-remove]', function (event, button) {
    event.preventDefault();
    changeLine(parseInt(button.getAttribute('data-line-remove'), 10), 0);
  });

  /* --------------------------------------------------- quantity steppers */
  on('click', '[data-qty-change]', function (event, button) {
    var wrap = button.closest('[data-qty]');
    var input = $('input', wrap);
    if (!input) return;

    var step = button.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;
    var next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    if (next === parseInt(input.value, 10)) return;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  on('change', '[data-qty] input', function (event, input) {
    var line = input.getAttribute('data-line');
    if (!line) return;
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 0;
    changeLine(parseInt(line, 10), Math.max(min, parseInt(input.value, 10) || min));
  });

  /* ----------------------------------------------------------- open/close */
  on('click', '[data-cart-open]', function (event) {
    // Let people cmd/ctrl-click through to the full cart page.
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    CartDrawer.open();
  });

  on('click', '[data-cart-close]', function (event) {
    event.preventDefault();
    CartDrawer.close();
  });

  /* -------------------------------------------------------- mobile menu */
  var MobileMenu = {
    releaseFocus: null,
    get el() { return $('[data-mobile-menu]'); },

    open: function () {
      var el = this.el;
      if (!el) return;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      scrollLock.lock();
      this.releaseFocus = trapFocus(el);
    },

    close: function () {
      var el = this.el;
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      scrollLock.unlock();
      if (this.releaseFocus) this.releaseFocus();
      var toggle = $('[data-menu-open]');
      if (toggle) toggle.focus();
    }
  };

  on('click', '[data-menu-open]', function (event) { event.preventDefault(); MobileMenu.open(); });
  on('click', '[data-menu-close]', function (event) { event.preventDefault(); MobileMenu.close(); });
  on('click', '[data-mobile-menu] a', function () { MobileMenu.close(); });

  document.addEventListener('click', function (event) {
    if (event.target.matches('[data-overlay]')) {
      CartDrawer.close();
      MobileMenu.close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      CartDrawer.close();
      MobileMenu.close();
    }
  });

  /* ------------------------------------------------------ product gallery */
  on('click', '[data-gallery-thumb]', function (event, thumb) {
    event.preventDefault();
    var gallery = thumb.closest('[data-gallery]');
    var stage = $('[data-gallery-main]', gallery);
    if (!stage) return;

    stage.innerHTML = thumb.getAttribute('data-media-html') || '';
    $$('[data-gallery-thumb]', gallery).forEach(function (el) {
      el.setAttribute('aria-current', String(el === thumb));
    });
  });

  /* ------------------------------------------------------- sticky header */
  var header = $('[data-header]');
  if (header) {
    var updateHeader = function () {
      header.classList.toggle('is-stuck', window.scrollY > 8);
    };
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  /* -------------------------------------------------------- scroll reveal */
  function initReveal(scope) {
    var items = $$('.reveal:not(.is-in)', scope || document);
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    items.forEach(function (el, index) {
      el.style.transitionDelay = Math.min(index % 4, 3) * 80 + 'ms';
      observer.observe(el);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    CartDrawer.syncCount();
  });

  /* Theme editor hooks */
  document.addEventListener('shopify:section:load', function (event) { initReveal(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    if (event.target.closest('[data-cart-drawer]')) CartDrawer.open();
  });
})();
